import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import "./VirtualEmployeesSection.css";
import Highlight from "@/components/Highlight/Highlight";
import { EMPLOYEE_AVATARS, EMPLOYEE_HUE } from "./employees";
import { useHireModal } from "@/components/HireModal/HireModalContext";
import { metaTechProvider } from "@/assets";
import { useGsapReveal } from "@/hooks/useGsapReveal";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { SOLUTION_PREFIX } from "@/i18n/config";
import { useLocale } from "@/i18n/useLocale";
import { CASE_PREFIX } from "@/i18n/config";
import { getCase, localizeCase } from "@/data/caseStudies";

/**
 * Casos reales que respaldan a un puesto. Solo donde hay un resultado medido de
 * verdad: el numero sale del propio caso (src/data/caseStudies.ts), no se
 * repite a mano aca. Los puestos sin caso no muestran prueba -no se inventa-.
 */
const PROOF_CASE: Record<string, string> = {
  comercial: "verona",
  recepcionista: "previnca",
};

const ICON_PROPS = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function CheckIcon() {
  return (
    <svg className="virtual-team__check" width="16" height="16" viewBox="0 0 24 24" strokeWidth="3" {...ICON_PROPS}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" strokeWidth="2.6" {...ICON_PROPS}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" strokeWidth="2.4" {...ICON_PROPS}>
      <path d={direction === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
    </svg>
  );
}

function ProofRow({ slug }: { slug: string }) {
  const { t, locale } = useLocale();
  const raw = getCase(slug);
  const study = raw ? localizeCase(raw, locale) : undefined;
  const stat = study?.stats?.[0];

  if (!study || !stat) return null;

  return (
    <div className="virtual-team__proof">
      <span className="virtual-team__proof-label">
        {t.virtualTeam.proofLabel} · {study.client}
      </span>
      <p className="virtual-team__proof-stat">
        <strong>{stat.value}</strong> {stat.label}
      </p>
      <Link to={`${CASE_PREFIX[locale]}/${study.slug}`} className="virtual-team__proof-link">
        {t.virtualTeam.proofLink}
      </Link>
    </div>
  );
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function VirtualEmployeesSection() {
  const { t, path, locale } = useLocale();
  const hire = useHireModal();
  const vt = t.virtualTeam;
  const contactHref = `${path("home")}#contacto`;
  const total = vt.members.length;

  const headerRef = useScrollReveal<HTMLDivElement>();
  const trackRef = useGsapReveal<HTMLUListElement>({ selector: ":scope > li", stagger: 0.1 });
  const metaRef = useScrollReveal<HTMLDivElement>();
  const customRef = useScrollReveal<HTMLDivElement>({ delay: 120 });

  const dialogs = useRef<Record<string, HTMLDialogElement | null>>({});
  const [pos, setPos] = useState({ active: 0, atStart: true, atEnd: false });

  // Puntos y flechas salen de la POSICION DE SCROLL, no de que tarjeta "entro"
  // al viewport. Con tres tarjetas a la vez, la ultima en entrar no es la
  // primera visible: "atras" apuntaba a una tarjeta que ya estaba en pantalla,
  // no habia nada que scrollear y el boton parecia roto (y al inicio figuraba
  // habilitado con el punto 3 activo). El punto es el avance proporcional del
  // scroll: en mobile (una tarjeta por vez) coincide con la tarjeta visible, y
  // en desktop va de 1 a N pasando por los intermedios.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const sync = () => {
      const max = track.scrollWidth - track.clientWidth;
      const next = {
        active: max > 0 ? Math.round((track.scrollLeft / max) * (total - 1)) : 0,
        atStart: track.scrollLeft <= 1,
        // Margen de 1px: los navegadores redondean scrollLeft.
        atEnd: track.scrollLeft >= max - 1,
      };
      setPos((prev) =>
        prev.active === next.active && prev.atStart === next.atStart && prev.atEnd === next.atEnd
          ? prev
          : next,
      );
    };

    const frame = requestAnimationFrame(sync);
    track.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);

    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
  }, [trackRef, total]);

  function scrollTrack(left: number) {
    trackRef.current?.scrollTo({ left, behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }

  // Posiciones de reposo de la pista: la de cada tarjeta, acotadas al maximo
  // (con tres tarjetas a la vez las ultimas no llegan a alinearse y el reposo
  // es el final del scroll). Las flechas saltan de una a otra con scrollTo
  // ABSOLUTO: un scrollBy relativo + scroll-snap obligatorio rebotaba al mismo
  // lugar al volver desde el final, y el click parecia no hacer nada.
  function stops(track: HTMLElement): number[] {
    const max = track.scrollWidth - track.clientWidth;
    const padLeft = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    const origin = track.getBoundingClientRect().left - track.scrollLeft + padLeft;

    const positions = Array.from(track.children).map((card) =>
      Math.round(Math.min(max, Math.max(0, card.getBoundingClientRect().left - origin))),
    );
    return [...new Set(positions)];
  }

  function step(direction: 1 | -1) {
    const track = trackRef.current;
    if (!track) return;

    // 4px de tolerancia: scrollLeft se redondea y el snap puede quedar a 1px.
    const here = track.scrollLeft;
    const list = stops(track);
    const target =
      direction === 1
        ? list.find((stop) => stop > here + 4)
        : [...list].reverse().find((stop) => stop < here - 4);

    if (target !== undefined) scrollTrack(target);
  }

  function goTo(index: number) {
    const track = trackRef.current;
    const card = track?.children[index] as HTMLElement | undefined;
    if (!track || !card) return;

    const target = stops(track)[Math.min(index, stops(track).length - 1)];
    scrollTrack(target);
  }

  return (
    <section className="virtual-team" id="empleados" aria-labelledby="virtual-team-title">
      <div className="virtual-team__glow" aria-hidden="true" />

      <div className="virtual-team__wrap virtual-team__header" ref={headerRef}>
        <p className="virtual-team__eyebrow">{vt.eyebrow}</p>
        <h2 id="virtual-team-title" className="virtual-team__title">
          <Highlight text={vt.title} className="virtual-team__accent" />
        </h2>
        <p className="virtual-team__lead">{vt.lead}</p>
      </div>

      <div role="region" aria-roledescription="carousel" aria-label={vt.carouselLabel}>
        <ul className="virtual-team__track" ref={trackRef}>
          {vt.members.map((member) => {
            const Avatar = EMPLOYEE_AVATARS[member.id];
            const cardStyle = { "--hue": EMPLOYEE_HUE[member.id] } as CSSProperties;

            return (
              <li key={member.id} className="virtual-team__card" style={cardStyle}>
                <div className="virtual-team__card-text">
                  <p className="virtual-team__card-eyebrow">{vt.card}</p>
                  <h3 className="virtual-team__role">{member.role}</h3>
                  <p className="virtual-team__tagline">{member.tagline}</p>
                </div>

                <div className="virtual-team__stage" aria-hidden="true">
                  <span className="virtual-team__aura" />
                  <div className="virtual-team__avatar">{Avatar && <Avatar />}</div>
                </div>

                <span className="virtual-team__pill" aria-hidden="true">
                  {vt.learnMore}
                  <span className="virtual-team__plus">
                    <PlusIcon />
                  </span>
                </span>

                <button
                  type="button"
                  className="virtual-team__open"
                  aria-haspopup="dialog"
                  aria-label={`${member.role}: ${vt.learnMore}`}
                  onClick={() => dialogs.current[member.id]?.showModal()}
                />

                {/* El detalle va en un <dialog> nativo por empleado: foco,
                    Escape y fondo modal vienen resueltos por el navegador, y
                    el texto queda en el HTML pre-renderizado (cerrado, no se
                    muestra pero se puede leer). */}
                <dialog
                  ref={(el) => {
                    dialogs.current[member.id] = el;
                  }}
                  className="virtual-team__dialog"
                  style={cardStyle}
                  aria-labelledby={`vt-dialog-${member.id}`}
                  onClick={(e) => {
                    // El click en el fondo oscuro le llega al propio <dialog>.
                    if (e.target === e.currentTarget) e.currentTarget.close();
                  }}
                >
                  <div className="virtual-team__dialog-layout">
                    <button
                      type="button"
                      className="virtual-team__close"
                      aria-label={vt.close}
                      onClick={() => dialogs.current[member.id]?.close()}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" strokeWidth="2.6" {...ICON_PROPS}>
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>

                    <div className="virtual-team__dialog-stage" aria-hidden="true">
                      <span className="virtual-team__aura" />
                      <div className="virtual-team__avatar">{Avatar && <Avatar />}</div>
                    </div>

                    <div className="virtual-team__dialog-body">
                      <p className="virtual-team__card-eyebrow">{vt.card}</p>
                      <h3 id={`vt-dialog-${member.id}`} className="virtual-team__dialog-role">
                        {member.role}
                      </h3>
                      <p className="virtual-team__quote">{member.quote}</p>

                      <ul className="virtual-team__skills">
                        {member.skills.map((skill) => (
                          <li key={skill}>
                            <CheckIcon />
                            <span>{skill}</span>
                          </li>
                        ))}
                      </ul>

                      {PROOF_CASE[member.id] && <ProofRow slug={PROOF_CASE[member.id]} />}

                      {/* Abre el modal rapido (nombre + celular) en vez de
                          mandar al formulario largo del final de la home. */}
                      <button
                        type="button"
                        className="virtual-team__hire"
                        onClick={() => {
                          dialogs.current[member.id]?.close();
                          hire.open(member.id);
                        }}
                      >
                        {vt.hire}
                      </button>
                    </div>
                  </div>
                </dialog>
              </li>
            );
          })}
        </ul>

        <div className="virtual-team__wrap virtual-team__controls">
          <div className="virtual-team__dots">
            {vt.members.map((member, index) => (
              <button
                key={member.id}
                type="button"
                className="virtual-team__dot"
                aria-label={member.role}
                aria-current={pos.active === index}
                onClick={() => goTo(index)}
              />
            ))}
          </div>

          <div className="virtual-team__arrows">
            <button
              type="button"
              className="virtual-team__arrow"
              aria-label={vt.prev}
              disabled={pos.atStart}
              onClick={() => step(-1)}
            >
              <ChevronIcon direction="left" />
            </button>
            <button
              type="button"
              className="virtual-team__arrow"
              aria-label={vt.next}
              disabled={pos.atEnd}
              onClick={() => step(1)}
            >
              <ChevronIcon direction="right" />
            </button>
          </div>
        </div>
      </div>

      <div className="virtual-team__wrap">
        <div className="virtual-team__meta" ref={metaRef}>
          <div className="virtual-team__meta-copy">
            <div className="virtual-team__meta-badges">
              <span className="virtual-team__meta-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" strokeWidth="2.2" {...ICON_PROPS}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                {vt.meta.badge}
              </span>

              {/* Sello oficial de Meta: respalda la frase "partner de Meta" con
                  la marca verificable, en vez de dejarla solo como texto. */}
              <img
                className="virtual-team__meta-logo"
                src={metaTechProvider}
                alt="Meta Tech Provider"
                width={128}
                height={48}
                loading="lazy"
                decoding="async"
              />
            </div>
            <h3 className="virtual-team__meta-title">
              <Highlight text={vt.meta.title} className="virtual-team__accent" />
            </h3>
            <p className="virtual-team__meta-text">{vt.meta.text}</p>
            <div className="virtual-team__meta-actions">
              <Link to={contactHref} className="virtual-team__hire virtual-team__meta-cta">
                {vt.meta.cta}
              </Link>
              {/* El detalle (coexistencia, chats sincronizados, canal oficial)
                  vive en la pagina de la solucion: aca solo se anuncia. */}
              <Link
                to={`${SOLUTION_PREFIX[locale]}/agente-ia-whatsapp`}
                className="virtual-team__meta-more"
              >
                {vt.meta.more} →
              </Link>
            </div>
          </div>
        </div>

        <div className="virtual-team__custom" ref={customRef}>
          <div className="virtual-team__custom-text">
            <h3 className="virtual-team__custom-title">{vt.custom.title}</h3>
            <p>{vt.custom.text}</p>
          </div>
          <Link to={contactHref} className="virtual-team__custom-cta">
            {vt.custom.cta}
          </Link>
        </div>
      </div>
    </section>
  );
}

export default VirtualEmployeesSection;
